import React, { useState } from 'react';
import { X, Loader2, PackageCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { suppliersAPI } from '../services/api';

// Admin: receive a purchase order line by line (partial deliveries allowed)
const ReceivePurchaseOrderModal = ({ po, onClose, onReceived }) => {
  const [qty, setQty] = useState(() => Object.fromEntries(po.lines.map(l => [l.product_id, Math.max(l.quantity - (l.received || 0), 0)])));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const total = Object.values(qty).reduce((n, v) => n + (parseInt(v, 10) || 0), 0);
  const surplus = po.lines.filter(l => (l.received || 0) + (parseInt(qty[l.product_id], 10) || 0) > l.quantity);

  const submit = async () => {
    if (total <= 0) { toast.error('Indiquez au moins une quantité reçue'); return; }
    if (surplus.length && !window.confirm(`Vous réceptionnez plus que commandé pour ${surplus.length} ligne(s). Confirmer le surplus ?`)) return;
    setBusy(true);
    try {
      const r = await suppliersAPI.receivePurchaseOrder(po.id, po.lines.map(l => ({ product_id: l.product_id, quantity: parseInt(qty[l.product_id], 10) || 0 })), note);
      toast.success(`+${r.added_units} unités en entrepôt · ${r.status}${r.remaining_units ? ` · reste ${r.remaining_units} attendue(s)` : ''}`);
      onReceived(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Réception impossible');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="receive-po-modal">
        <div className="p-5 border-b bg-gradient-to-r from-green-600 to-green-700 text-white flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2"><PackageCheck size={20} /> Réceptionner {po.number} — {po.supplier_name}</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="receive-close"><X size={22} /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]" data-testid="receive-lines-table">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr><th className="px-3 py-2 text-left">Produit</th><th className="px-3 py-2 text-right">Commandé</th><th className="px-3 py-2 text-right">Déjà reçu</th><th className="px-3 py-2 text-right">Reste attendu</th><th className="px-3 py-2 text-right w-32">Reçu aujourd'hui</th></tr>
              </thead>
              <tbody className="divide-y">
                {po.lines.map(l => {
                  const rest = Math.max(l.quantity - (l.received || 0), 0);
                  const v = parseInt(qty[l.product_id], 10) || 0;
                  return (
                    <tr key={l.product_id} data-testid={`receive-line-${l.product_id}`} className={rest === 0 ? 'text-gray-400' : ''}>
                      <td className="px-3 py-2"><p className="font-medium text-gray-900">{l.name}</p><p className="text-xs text-gray-400">{l.brand} · Réf. {l.ref}{l.supplier_ref ? ` · ${l.supplier_ref}` : ''}</p></td>
                      <td className="px-3 py-2 text-right">{l.quantity}</td>
                      <td className="px-3 py-2 text-right">{l.received || 0}</td>
                      <td className="px-3 py-2 text-right font-semibold" data-testid={`receive-rest-${l.product_id}`}>{rest}</td>
                      <td className="px-3 py-2 text-right">
                        <Input data-testid={`receive-qty-${l.product_id}`} type="number" min="0" value={qty[l.product_id]} onChange={(e) => setQty(q => ({ ...q, [l.product_id]: e.target.value }))} className={`h-8 text-right ${v > rest ? 'border-amber-400' : ''}`} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {surplus.length > 0 && <p className="text-xs text-amber-700 flex items-center gap-1" data-testid="receive-surplus-warning"><AlertTriangle size={14} /> Surplus livré sur {surplus.length} ligne(s) : le stock sera tout de même incrémenté.</p>}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Note (n° de BL, transporteur…)</label>
            <Input data-testid="receive-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="BL 2026-0451, colis 1/2" />
          </div>
        </div>
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between gap-3">
          <p className="text-sm text-gray-700" data-testid="receive-total"><strong>{total}</strong> unité{total > 1 ? 's' : ''} à ajouter au stock entrepôt</p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} data-testid="receive-cancel">Annuler</Button>
            <Button data-testid="receive-submit" onClick={submit} disabled={busy || total <= 0} className="bg-green-600 hover:bg-green-700">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <PackageCheck size={16} className="mr-2" />} Valider la réception</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceivePurchaseOrderModal;
